import { useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { Person } from '@/account/arcade-api';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { CreditSummary } from '@/components/CreditSummary/CreditSummary';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RangePicker } from '@/components/RangePicker/RangePicker';
import { Segmented } from '@/components/Segmented/Segmented';
import { Spinner } from '@/components/Spinner/Spinner';
import { UsageChart } from '@/components/UsageChart/UsageChart';
import { UsageKeyTable } from '@/components/UsageKeyTable/UsageKeyTable';
import { USAGE } from '@/usage/api';
import { formatNumber } from '@/usage/format';
import {
  buildChart,
  clipToKeys,
  localDate,
  presetRange,
  requestWindow,
  startOfDay,
  type DayRange,
  type Metric,
  type Preset,
} from '@/usage/series';

import styles from './UsageContainer.module.css';

const POLL_MS = 60_000;
const dayMonth = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' });

/** The signed-in home page: this month's credits and usage by API key. */
export function UsageContainer({ person }: { person: Person }) {
  const navigate = useNavigate();
  const [now] = useState(() => new Date());
  const today = localDate(now);
  const [preset, setPreset] = useState<Preset | null>('7d');
  const [custom, setCustom] = useState<DayRange>(() => presetRange('7d', now));
  const [metric, setMetric] = useState<Metric>('credits');
  const [showRevoked, setShowRevoked] = useState(false);
  const range = preset ? presetRange(preset, now) : custom;

  const { data, previousData, loading, error, refetch } = useQuery(USAGE, {
    variables: requestWindow(range),
    fetchPolicy: 'cache-and-network',
    pollInterval: POLL_MS,
  });
  const shown = data ?? previousData;
  const keys = useMemo(() => shown?.apiKeys ?? [], [shown]);
  const revokedCount = keys.filter((key) => key.revokedAt).length;
  const visibleKeys = useMemo(
    () => (showRevoked ? keys : keys.filter((key) => !key.revokedAt)),
    [keys, showRevoked],
  );
  const clipped = shown ? clipToKeys(range, visibleKeys) : null;
  const chart = useMemo(
    () => (clipped ? buildChart(keys, clipped, { includeRevoked: showRevoked }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the range is compared by value
    [keys, showRevoked, clipped?.from, clipped?.to],
  );

  const firstKey = clipToKeys({ from: '0000-01-01', to: today }, visibleKeys);
  const firstKeyDay = firstKey ? dayMonth.format(startOfDay(firstKey.from)) : null;

  const totals = chart?.series.reduce(
    (sum, entry) => ({
      credits: sum.credits + entry.totalCredits,
      requests: sum.requests + entry.totalRequests,
    }),
    { credits: 0, requests: 0 },
  );

  return (
    <div data-fill-viewport>
      <PageHeader
        className={styles.header}
        title="Usage"
        lede={`${person.firstName ? `Welcome back, ${person.firstName}. ` : ''}Credits and requests across your API keys, in your local time.`}
      />

      {!shown && loading ? <Spinner /> : null}
      {error && !shown ? (
        <Callout tone="danger" title="Could not load your usage">
          <Button variant="ghost" onClick={() => void refetch()}>
            Try again
          </Button>
        </Callout>
      ) : null}

      {shown ? (
        <>
          <CreditSummary
            allowance={shown.balance.allowance}
            spent={shown.balance.spent}
            available={shown.balance.available}
            periodStart={new Date(shown.balance.periodStart)}
            periodEnd={new Date(shown.balance.periodEnd)}
            now={now}
          />

          {keys.length === 0 ? (
            <Callout title="No API keys yet">
              Usage appears here once you make calls with an API key.
              <div className={styles.emptyActions}>
                <Button onClick={() => navigate('/settings/api-keys')}>Create an API key</Button>
                <Button variant="ghost" onClick={() => navigate('/playground')}>
                  Try the playground
                </Button>
              </div>
            </Callout>
          ) : (
            <section className={styles.card} aria-labelledby="usage-heading">
              <h2 id="usage-heading" className={styles.visuallyHidden}>
                Usage over time
              </h2>
              <div className={styles.controls}>
                <RangePicker
                  range={range}
                  preset={preset}
                  today={today}
                  onPreset={setPreset}
                  onCustom={() => {
                    setCustom(range);
                    setPreset(null);
                  }}
                  onRange={(next) => {
                    setCustom(next);
                    setPreset(null);
                  }}
                />
                <div className={styles.view}>
                  <Segmented
                    label="Measure"
                    options={[
                      { value: 'credits', label: `${formatNumber(totals?.credits ?? 0)} credits` },
                      {
                        value: 'requests',
                        label: `${formatNumber(totals?.requests ?? 0)} requests`,
                      },
                    ]}
                    value={metric}
                    onChange={setMetric}
                  />
                  {revokedCount > 0 ? (
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={showRevoked}
                        onChange={(event) => setShowRevoked(event.target.checked)}
                      />
                      Show revoked keys ({revokedCount})
                    </label>
                  ) : null}
                </div>
              </div>

              {chart && totals ? (
                <>
                  {clipped && clipped.from !== range.from ? (
                    <p className={styles.note}>
                      Since {dayMonth.format(startOfDay(clipped.from))}, when your first API key was
                      created.
                    </p>
                  ) : null}
                  <UsageChart data={chart} metric={metric} stale={loading && !data} fill />
                  <UsageKeyTable
                    series={chart.series}
                    metric={metric}
                    renderName={(entry, name) => (
                      <Link to={`/settings/api-keys/${entry.id}`}>{name}</Link>
                    )}
                  />
                </>
              ) : (
                <p className={styles.none}>
                  {firstKeyDay
                    ? `You had no API keys in this period. Pick a range from ${firstKeyDay} onwards.`
                    : 'All of your API keys are revoked. Show revoked keys to see their usage.'}
                </p>
              )}
            </section>
          )}
        </>
      ) : null}
    </div>
  );
}
